import React, { useContext, useState } from 'react';
import { Badge, Card, CardHeader, CardBody } from 'reactstrap';

import AppContext from '../AppContext';
import dataManager from '../DataManager';
import Utils from '../Utils';
import Icon from './Icon';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faInbox as Inbox, faArchive as Archive, faTrashAlt as Trash } from '@fortawesome/free-solid-svg-icons';
import { faThumbtack as Pin} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';

import styles from './InfoItem.module.css';

function InfoItem({ record, states }) {
    const { t } = useContext(AppContext);

    const handleTitleClick = (record) => {
        if (states.expandedRecord === record.id) {
            states.setExpandedRecord(null);
        } else {
            states.setExpandedRecord(record.id);
        }

        if (record.data.category !== 'Trash') {
            states.setEditingTitleId(record.id);
            states.setEditableTitle(record.data.title);
        }
    };

    const handleTitleChange = (e) => {
        console.log("title changed");
        states.setEditableTitle(e.target.value);
    };

    const handleTitleEditingDone = (record) => {
        states.setEditingTitleId(null);
        if (record.data.title !== states.editableTitle) {
            console.log("saving due to title change");
            dataManager.updateRecord(record.id, { title: states.editableTitle }, true);
        }
    };

    const toggleStarred = (e, record) => {
        e.stopPropagation(); // Prevent click event from propagating to the card
        dataManager.updateRecord(record.id, { starred: !record.data.starred });
    };

    function ContentRenderer({ id, data }) {
        const { type, content } = data;
        const [text, setText] = useState(content);

        const handleChange = (e) => {
            setText(e.target.value);
        };

        const handleTextEditingDone = () => {
            if (content !== text) {
                console.log("saving due to content text change");
                dataManager.updateRecord(id, { content: text }, true);
            }
        };

        if (type === "Text") {
            return <textarea value={text} placeholder={t('placeholder.notes')} className={`form-control ${styles.contentInput}`}
                onChange={handleChange}
                onBlur={handleTextEditingDone}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.shiftKey) {
                        e.preventDefault();
                        handleTextEditingDone();
                    }
                }}
                disabled={data.category === 'Trash'}
            />;
        }
        if (type === "Image") {
            return <img src={`data:image/jpeg;base64,${content}`} alt={t('alt.savedImage')} className={`img-fluid ${styles.imageContent}`} />;
        }
        return <p>Unsupported content type.</p>;
    }

    const iconMapping = { Inbox, Archive, Trash };

    const MoveButton = ({ name, record }) => {
        return (
            <FontAwesomeIcon
                icon={iconMapping[name]}
                className={styles.button}
                title={t(`command.moveTo${name}`)}
                onClick={(e) => {
                    e.stopPropagation();
                    dataManager.moveRecord(record.id, name);
                }}
            />
        );
    };

    const PinButton = ({ record }) => {
        const { pinned } = record.data;
        return (
            <FontAwesomeIcon
                icon={Pin}
                className={`${pinned ? styles.offButton : ""} ${styles.button}`}
                title={t(`command.${pinned ? "unpin" : "pin"}`)}
                onClick={(e) => {
                    e.stopPropagation();
                    dataManager.pinRecord(record.id, !pinned);
                }}
            />
        );
    };

    const DeleteButton = ({ record }) => {
        return (
            <FontAwesomeIcon
                icon={Trash}
                className={`${styles.button} ${styles.dangerousButton}`}
                title={t("command.permanentlyDelete")}
                onClick={(e) => {
                    e.stopPropagation();
                    dataManager.deleteRecord(record.id);
                }}
            />
        );
    };

    const MAX_LEN = 100;

    const getDisplayedTitle = (data) => {
        const { type, title, content } = data;
        if (title) {
            return Utils.truncate(title, MAX_LEN);
        }
        if (type === "Image") {
            return t("title.untitledImage");
        }
        return Utils.truncate(content, MAX_LEN);
    };

    return (
        <Card key={record.id} className={`${styles.item}`}>
            <CardHeader className="d-flex" onClick={() => handleTitleClick(record)}>
                <FontAwesomeIcon
                    icon={record.data.starred ? faStarSolid : faStarRegular}
                    className={styles.starIcon}
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent click event from propagating to the CardHeader
                        toggleStarred(e, record);
                    }}
                />
                {states.editingTitleId === record.id ? (
                    <input
                        type="text"
                        placeholder={t('placeholder.title')}
                        value={states.editableTitle}
                        onChange={handleTitleChange}
                        onBlur={() => handleTitleEditingDone(record)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleTitleEditingDone(record); }}
                        className={`border-0 ${styles.titleInput}`}
                        disabled={record.data.category === 'Trash'}
                        autoFocus
                    />
                ) : (
                    <>
                        <Icon type={record.data.type} content={record.data.content} />
                            <span>{getDisplayedTitle(record.data)}
                            {Utils.getUrl(record.data.content) && (
                                <FontAwesomeIcon
                                    icon={faExternalLinkAlt}
                                    className={`ml-2 ${styles.linkIcon}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(record.data.content, '_blank', 'noopener,noreferrer');
                                    }}
                                />
                            )}
                        </span>
                        <div className={styles.buttons}>
                            <PinButton record={record} />
                            {record.data.category !== 'Inbox' && (<MoveButton name='Inbox' record={record} />)}
                            {record.data.category === 'Inbox' && (<MoveButton name='Archive' record={record} />)}
                            {record.data.category !== 'Trash' && (<MoveButton name='Trash' record={record} />)}
                            {record.data.category === 'Trash' && (<><MoveButton name='Archive' record={record} /><DeleteButton record={record} /></>)}
                        </div>
                    </>
                )}
            </CardHeader>
            {states.expandedRecord === record.id && (
                <CardBody>
                    <ContentRenderer {...record} />
                    <Badge color="info" className={`mr-2 ${styles.date}`}>
                        Created: {Utils.simplifyDate(record.data.created)}, Updated: {Utils.simplifyDate(record.data.updated)}
                    </Badge>
                </CardBody>
            )}
        </Card>
    );
}

export default InfoItem;
